import React, { useState, useContext } from 'react';
import { signInWithPopup, signInWithRedirect, getRedirectResult, getIdToken } from 'firebase/auth';
import { auth, googleProvider } from '../utils/firebase';
import { AuthContext } from '../contexts/AuthContext';
import { resetPassword as apiResetPassword, googleSignIn as apiGoogleSignIn } from '../utils/api';
import { extractErrorMessage, shouldSuggestSignUp, shouldSuggestSignIn } from '../utils/errorMessages';

export default function AuthModal({ initialTab = 'login', onClose }) {
  const { authenticate, loading } = useContext(AuthContext);
  const [mode, setMode] = useState(initialTab);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [suggestSignUp, setSuggestSignUp] = useState(false);
  const [suggestSignIn, setSuggestSignIn] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSuggestSignUp(false);
    setSuggestSignIn(false);
    
    if (mode === 'reset') {
      await handlePasswordReset();
      return;
    }
    
    // Validate password confirmation for registration
    if (mode === 'register') {
      if (pass !== confirmPass) {
        setError('Passwords do not match');
        return;
      }
      if (pass.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }
    
    const credentials = mode === 'register' 
      ? { 
          email, 
          password: pass, 
          first_name: firstName.trim() || undefined,
          last_name: lastName.trim() || undefined
        }
      : { email, password: pass };
    
    const result = await authenticate(credentials, mode);
    if (result.success) {
      onClose();
    } else {
      setError(result.error);
      setSuggestSignUp(shouldSuggestSignUp(result.originalError));
      setSuggestSignIn(shouldSuggestSignIn(result.originalError));
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      await apiResetPassword(email);
      setMessage('Password reset email sent! Check your inbox.');
      setError('');
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setMessage('');
    setGoogleLoading(true);
    setSuggestSignUp(false);
    setSuggestSignIn(false);
    
    try {
      let result;
      
      try {
        // Try popup first
        result = await signInWithPopup(auth, googleProvider);
      } catch (popupError) {
        console.log('Popup blocked, trying redirect...', popupError);
        
        // If popup fails (likely due to COOP), try redirect
        if (popupError.code === 'auth/popup-blocked' || 
            popupError.message?.includes('Cross-Origin-Opener-Policy')) {
          
          // Use redirect method instead
          await signInWithRedirect(auth, googleProvider);
          return; // The redirect will handle the rest
        }
        
        throw popupError; // Re-throw if it's a different error
      }
      
      const idToken = await getIdToken(result.user);
      
      // Send the token to your backend
      const resp = await apiGoogleSignIn(idToken);
      
      // Handle successful authentication
      localStorage.setItem('jwt_token', resp.data.idToken);
      onClose();
      window.location.reload(); // Simple way to refresh auth state
      
    } catch (err) {
      console.error('Google sign-in error:', err);
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-2xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {mode === 'login' ? 'Sign In' : mode === 'register' ? 'Sign Up' : 'Reset Password'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Success Message */}
          {message && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-600">{message}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message with Suggestions */}
          {error && (
            <div className="mb-4 space-y-3">
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              </div>
              
              {/* Suggestion to sign up */}
              {suggestSignUp && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-700">
                    Don't have an account? 
                    <button 
                      onClick={() => {
                        setMode('register');
                        setError('');
                        setSuggestSignUp(false);
                        setSuggestSignIn(false);
                      }}
                      className="ml-1 font-medium text-blue-600 hover:text-blue-500"
                    >
                      Sign up here
                    </button>
                  </p>
                </div>
              )}
              
              {/* Suggestion to sign in */}
              {suggestSignIn && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-700">
                    Already have an account? 
                    <button 
                      onClick={() => {
                        setMode('login');
                        setError('');
                        setSuggestSignUp(false);
                        setSuggestSignIn(false);
                      }}
                      className="ml-1 font-medium text-green-600 hover:text-green-500"
                    >
                      Sign in here
                    </button>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="mb-4 grid grid-cols-3 rounded-lg bg-gray-100 p-1">
            {['login', 'register', 'reset'].map((key) => (
              <button
                key={key}
                onClick={() => {
                  setMode(key);
                  setError('');
                  setMessage('');
                  setFirstName('');
                  setLastName('');
                  setConfirmPass('');
                  setSuggestSignUp(false);
                  setSuggestSignIn(false);
                }}
                className={`rounded-md py-2 text-xs font-medium transition
                  ${
                    mode === key
                      ? 'bg-white shadow text-purple-600'
                      : 'text-gray-500 hover:text-purple-600'
                  }`}
              >
                {key === 'login' ? 'Login' : key === 'register' ? 'Register' : 'Reset'}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === 'register' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
              />
            </div>

            {mode !== 'reset' && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder={mode === 'register' ? 'Min 6 characters' : ''}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600"
                />
              </div>
            )}

            {mode === 'register' && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPass}
                  onChange={(e) => setConfirmPass(e.target.value)}
                  className={`mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 ${
                    confirmPass && pass !== confirmPass ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {confirmPass && (
                  <p className={`mt-1 text-xs ${pass === confirmPass ? 'text-green-600' : 'text-red-600'}`}>
                    {pass === confirmPass ? '✅ Passwords match' : '❌ Passwords do not match'}
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-br from-purple-400 to-purple-600 text-white rounded-md shadow-md hover:from-purple-500 hover:to-purple-700 disabled:opacity-50 transition"
            >
              {loading 
                ? 'Working…' 
                : mode === 'login' 
                  ? 'Login' 
                  : mode === 'register' 
                    ? 'Create account'
                    : 'Send reset email'
              }
            </button>
          </form>

          {mode !== 'reset' && (
            <>
              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">Or continue with</span>
                </div>
              </div>

              {/* Google Sign-in Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading || googleLoading}
                className="w-full flex items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {googleLoading ? 'Signing in...' : 'Sign in with Google'}
              </button>
            </>
          )}

          <div className="text-center text-sm text-gray-600 mt-4">
            {mode === 'login' ? (
              <>
                Need an account?{' '}
                <button
                  className="text-purple-600 hover:underline"
                  onClick={() => {
                    setMode('register');
                    setError('');
                    setMessage('');
                  }}
                >
                  Register
                </button>
                {' · '}
                <button
                  className="text-purple-600 hover:underline"
                  onClick={() => {
                    setMode('reset');
                    setError('');
                    setMessage('');
                  }}
                >
                  Forgot password?
                </button>
              </>
            ) : mode === 'register' ? (
              <>
                Already registered?{' '}
                <button
                  className="text-purple-600 hover:underline"
                  onClick={() => {
                    setMode('login');
                    setError('');
                    setMessage('');
                  }}
                >
                  Login
                </button>
              </>
            ) : (
              <>
                Remember your password?{' '}
                <button
                  className="text-purple-600 hover:underline"
                  onClick={() => {
                    setMode('login');
                    setError('');
                    setMessage('');
                  }}
                >
                  Back to login
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
